import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { CarIcon, LockKeyIcon, UserIcon, ArrowRightIcon, WarningCircleIcon, WhatsappLogoIcon, PhoneIcon } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BG =
  "https://images.unsplash.com/photo-1760199129864-df2332e3f3e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxzcG9ydHMlMjBjYXIlMjBkYXJrJTIwZ2FyYWdlfGVufDB8fHx8MTc4NTYwMDE0OHww&ixlib=rb-4.1.0&q=85";

const formatError = (detail) => {
  if (!detail) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

export const LoginPage = () => {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await login(username, password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || t("loginFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#0A0A0A] text-white">
      <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-e from-black/95 via-black/80 to-black/40" />

      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="flex items-center justify-between px-6 sm:px-8 py-6">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-400">Fleet</div>
            <div className="text-2xl font-bold leading-none">{t("appName")}</div>
          </div>
          <LanguageToggle />
        </div>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] items-center px-6 sm:px-8 lg:px-16 gap-12 pb-16">
          <div className="hidden lg:block">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-6">/01 · {t("tagline")}</div>
            <h1 className="text-5xl xl:text-6xl font-bold leading-none tracking-tight mb-6">
              {t("signInHeadline")}
            </h1>
            <p className="text-lg text-zinc-300 max-w-lg leading-relaxed mb-10">{t("signInSub")}</p>

            <div
              data-testid="subscribe-cta-desktop"
              className="inline-flex flex-col gap-3 border border-white/10 bg-black/40 backdrop-blur p-5 max-w-md"
            >
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-400">{t("subscribeCta")}</div>
              <div className="flex items-center gap-4 flex-wrap">
                <a
                  href="tel:+96893803727"
                  data-testid="contact-phone-link"
                  dir="ltr"
                  className="inline-flex items-center gap-2 text-2xl font-bold text-white hover:opacity-80 transition-opacity"
                >
                  <PhoneIcon size={22} weight="bold" />
                  {t("contactPhone")}
                </a>
                <a
                  href="https://wa.me/96893803727"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-whatsapp-link"
                  className="inline-flex items-center gap-1.5 border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 px-3 py-1.5 text-xs uppercase tracking-[0.2em] transition-colors"
                >
                  <WhatsappLogoIcon size={14} weight="bold" /> WhatsApp
                </a>
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="login-form"
            className="w-full max-w-md mx-auto lg:mx-0 lg:ms-auto border border-white/10 bg-[#0A0A0A]/80 backdrop-blur-xl p-6 sm:p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 border border-white/10 bg-[#121212]">
                <CarIcon size={22} weight="bold" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">/access</div>
                <h2 className="text-2xl font-bold">{t("loginTitle")}</h2>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("username")}</Label>
                <div className="relative">
                  <UserIcon size={14} className="absolute top-1/2 -translate-y-1/2 start-3 text-zinc-500" />
                  <Input
                    data-testid="login-username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="bg-[#121212] border-white/10 ps-9"
                    autoComplete="username"
                    required
                    dir="ltr"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("password")}</Label>
                <div className="relative">
                  <LockKeyIcon size={14} className="absolute top-1/2 -translate-y-1/2 start-3 text-zinc-500" />
                  <Input
                    data-testid="login-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-[#121212] border-white/10 ps-9"
                    autoComplete="current-password"
                    required
                  />
                </div>
              </div>

              {error && (
                <div
                  data-testid="login-error"
                  className="flex items-start gap-2 border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-3 text-sm text-[#FF3B30]"
                >
                  <WarningCircleIcon size={16} weight="bold" className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                data-testid="login-submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-white text-black px-6 py-3 font-semibold hover:bg-zinc-100 disabled:opacity-60 transition-colors"
              >
                {loading ? "…" : t("signIn")}
                {!loading && <ArrowRightIcon size={16} weight="bold" className="rtl-flip" />}
              </button>

              <p className="text-xs text-zinc-500 text-center leading-relaxed">
                {t("contactAdmin")}
              </p>

              <div
                data-testid="subscribe-cta-mobile"
                className="lg:hidden border-t border-white/10 pt-4 mt-2 space-y-2 text-center"
              >
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{t("subscribeCta")}</div>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <a
                    href="tel:+96893803727"
                    dir="ltr"
                    className="inline-flex items-center gap-2 font-bold text-white hover:opacity-80 transition-opacity"
                  >
                    <PhoneIcon size={16} weight="bold" />
                    {t("contactPhone")}
                  </a>
                  <a
                    href="https://wa.me/96893803727"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 px-2 py-1 text-[10px] uppercase tracking-[0.2em] transition-colors"
                  >
                    <WhatsappLogoIcon size={12} weight="bold" /> WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </form>
        </div>

        <footer className="px-6 sm:px-8 lg:px-16 py-5 border-t border-white/10 text-center" data-testid="login-footer">
          <div className="text-xs text-zinc-400 tracking-wide">
            © {new Date().getFullYear()} · {t("footerRights")}
          </div>
        </footer>
      </div>
    </div>
  );
};
